import { useId, type SVGProps } from 'react';

type DoopeyMarkProps = SVGProps<SVGSVGElement> & {
  decorative?: boolean;
  label?: string;
};

/**
 * Doopey's World mark: layered landscape tiles clipped into a compact D.
 *
 * The unified form names Doopey at a squint; the six landing-page section
 * colors read as sky, terrain, water, and light on a second look.
 */
export function DoopeyMark({
  decorative = false,
  label = "Doopey's World",
  ...props
}: DoopeyMarkProps) {
  const instanceId = useId().replace(/:/g, '');
  const clipId = `${instanceId}-doopey-shape`;
  const seamMaskId = `${instanceId}-doopey-seams`;

  return (
    <svg
      viewBox="53 40 388 394"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : label}
      role={decorative ? undefined : 'img'}
      {...props}
    >
      {!decorative && <title>{label}</title>}
      <defs>
        <clipPath id={clipId}>
          <path d="M 61 95 Q 61 48 118 48 L 305 48 C 355 55 393 88 414 138 C 427 170 433 205 433 240 C 433 292 414 332 388 362 C 352 404 302 426 248 426 L 102 426 Q 61 426 61 388 Z" />
        </clipPath>
        <mask id={seamMaskId}>
          <rect x="0" y="0" width="512" height="496" fill="white" />
          <g
            stroke="black"
            strokeWidth="8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M 40 92 Q 120 103 186 112 Q 250 92 320 54 L 330 36" />
            <path d="M 40 170 Q 130 196 198 213 L 251 216 Q 330 176 424 143 L 440 137" />
            <path d="M 251 216 Q 340 250 436 286 L 446 290" />
            <path d="M 380 268 L 227 343" />
            <path d="M 227 343 Q 135 300 40 280" />
            <path d="M 227 343 L 96 434 L 88 444" />
          </g>
        </mask>
      </defs>
      <g clipPath={`url(#${clipId})`} mask={`url(#${seamMaskId})`}>
        <path fill="var(--doopey-amber, #f8b41d)" d="M 40 92 L 40 20 L 350 20 L 328 38 L 320 54 Q 250 92 186 112 Q 120 103 40 92 Z" />
        <path fill="var(--doopey-blue, #02b5f9)" d="M 40 92 Q 120 103 186 112 Q 250 92 320 54 L 330 36 L 350 20 L 480 20 L 480 160 L 440 137 L 424 143 Q 330 176 251 216 L 198 213 Q 130 196 40 170 Z" />
        <path fill="var(--doopey-green, #50dd73)" d="M 251 216 Q 330 176 424 143 L 440 137 L 480 128 L 480 292 L 446 290 L 436 286 Q 340 250 251 216 Z" />
        <path fill="var(--doopey-rose, #d14d7a)" d="M 40 170 Q 130 196 198 213 L 251 216 Q 340 250 380 268 L 227 343 Q 135 300 40 280 Z" />
        <path fill="var(--doopey-cyan, #0dc0de)" d="M 40 280 Q 135 300 227 343 L 96 434 L 88 450 L 40 450 Z" />
        <path fill="var(--doopey-purple, #745ffb)" d="M 96 434 L 227 343 L 380 268 Q 400 276 436 286 L 470 290 L 470 350 L 400 440 L 260 478 L 90 444 Z" />
      </g>
    </svg>
  );
}
